import { StripePaymentConfigTypes } from "./../../../components/payments/components/StripePayment";
import { apiClient } from "../../../services/axiosInstance";
import {
  Comic,
  Fragment,
  ReactionSummaryEntity,
  Release,
  BlogCommentEntity,
  ServerListResponse,
  ServerSingleResponse,
} from "../../../types";
import {
  AccessData,
  ReaderNavigationContext,
  ToggleReactionPayload,
  TrackViewPayload,
} from "../types";
import { buildContextQueryParams } from "../../../utils/buildContextQueryParams";

export const GET_RELEASE_FRAGMENTS_QUERY_KEY = ["get-release-fragments"];
export function getReleaseFragments(releaseId: number) {
  return apiClient.get<ServerListResponse<Fragment>>(
    `/v1/releases/${releaseId}/fragments`,
  );
}

export const GET_COMIC_QUERY_KEY = ["get-comic"];
export function getComic(comicSlug: string) {
  return apiClient.get<ServerSingleResponse<Comic>>(`/v1/read/${comicSlug}`);
}

export const GET_RELEASE_QUERY_KEY = ["get-release"];

export function getRelease(
  uuid: string,
  navigationContext?: ReaderNavigationContext,
  freeOnly = false,
) {
  const contextQueryParams = buildContextQueryParams(navigationContext);
  const params = freeOnly
    ? `${contextQueryParams}&freeOnly=1`
    : contextQueryParams;

  return apiClient.get<ServerSingleResponse<Release>>(
    `/v1/readV2/${uuid}?${params}`,
  );
}

export const GET_COMIC_RELEASES_QUERY_KEY = ["get-comic-releases"];
export function getComicReleases(comicId: number, langId?: string) {
  return apiClient.get<ServerListResponse<Release>>(
    `/v1/comics/${comicId}/releases?lang_id=${langId ?? ""}&all=true`,
  );
}

export function getSimilarComicReleases(comicId: number) {
  return apiClient.get<ServerListResponse<Comic>>(
    `/v1/comics/${comicId}/similar`,
  );
}

export const GET_RELEASE_COMMMENTS = ["get-release-comments"];
export function getReleaseComments(releaseId: number) {
  return apiClient.get<ServerListResponse<BlogCommentEntity>>(
    `/v1/releases/${releaseId}/comments`,
  );
}

export function postTrackPageView(
  releaseId: number,
  payload: TrackViewPayload,
) {
  return apiClient.post<ServerSingleResponse<AccessData>>(
    `/v1/releases/${releaseId}/track-view`,
    { meta: [], payload },
  );
}

export function postToggleReaction(
  releaseId: number,
  payload: ToggleReactionPayload,
) {
  return apiClient.post<ServerSingleResponse<ReactionSummaryEntity>>(
    `/v1/releases/${releaseId}/toggle-reaction`,
    { meta: [], payload },
  );
}

type ReleaseCommentPayload = {
  body: string;
  parentId?: number;
};
export function postReleaseComment(
  releaseId: number,
  payload: ReleaseCommentPayload,
) {
  return apiClient.post<ServerSingleResponse<BlogCommentEntity>>(
    `/v1/releases/${releaseId}/post-comment`,
    { meta: [], payload: { body: payload.body, parent_id: payload.parentId } },
  );
}

export function toggleCommentReaction(
  articleId: number,
  commentId: number,
  reactionTypeId: number,
) {
  return apiClient.post(
    `/v1/releases/${articleId}/toggle-comment-reaction/${commentId}`,
    {
      meta: {},
      payload: {
        reaction_type_id: reactionTypeId,
      },
    },
  );
}

export function editReleaseComment(data: {
  releaseId: number;
  commentId: number;
  body: string;
}) {
  return apiClient.post<ServerSingleResponse<BlogCommentEntity>>(
    `/v1/releases/${data.releaseId}/edit-comment/${data.commentId}`,
    {
      meta: {},
      payload: {
        body: data.body,
      },
    },
  );
}

export function deleteReleaseComment(data: {
  releaseId: number;
  commentId: number;
  deleteReason?: string;
}) {
  return apiClient.post<ServerSingleResponse<BlogCommentEntity>>(
    `/v1/releases/${data.releaseId}/delete-comment/${data.commentId}`,
    {
      meta: {},
      payload: {
        delete_reason: data.deleteReason || "",
      },
    },
  );
}

type PurchaseReleasePDFPayload = StripePaymentConfigTypes & {
  netAmount: number;
};
export function postReleasePDFPurchase(
  releaseId: number,
  payload: PurchaseReleasePDFPayload,
) {
  return apiClient.post<ServerSingleResponse<any>>(
    `/v1/releases/${releaseId}/purchase`,
    { meta: [], payload },
  );
}
